/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useCallback } from "react";
import "./Gallery.css";
import { useConfig } from "../../context/ConfigContext";

function isImage(fileName) {
  return /\.(jpe?g|png|gif|bmp|svg|webp)$/i.test(fileName);
}
function isVideo(fileName) {
  return /\.(mp4|mov|avi|mkv|webm)$/i.test(fileName);
}

export default function Gallery() {
  const { config } = useConfig();
  const API_URL = config.REACT_APP_GETALLPHOTOS;
  const API_KEY = config.REACT_APP_API_KEY;

  const PAGE_SIZE = 9;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [files, setFiles] = useState([]);

useEffect(() => {
  const fetchData = async () => {
    try {

      const res = await fetch(API_URL, {
        headers: {
          "Api-Key": API_KEY
        }
      });

      const data = await res.json();

      // Convert your API Images array → gallery format
      const formatted = data.data.map((img, index) => {
        const src = img.url;
        return {
          id: index + 1,
          src: `https://bb3memorial.co.uk/Images/${src.split("/")[4]}`,
          type: isImage(img.name)
            ? "image"
            : isVideo(img.name)
            ? "video"
            : "unknown",
          caption: img.comment || img.caption,
          size: img.size,
        };
      });

      setFiles(formatted);
    } catch (err) {
      console.error(err);
    }
  };

  fetchData();
}, []);



  const items = useMemo(() => files.slice(0, visibleCount), [files, visibleCount]);

  const lightbox = lightboxIndex !== null ? items[lightboxIndex] : null;

  const openItem = (item) =>
    setLightboxIndex(items.findIndex((m) => m.id === item.id));
  const closeLightbox = () => setLightboxIndex(null);

  const showPrev = useCallback(() => {
    setLightboxIndex((i) =>
      i > 0 ? i - 1 : items.length - 1 // wrap around
    );
  }, [items]);

  const showNext = useCallback(() => {
    setLightboxIndex((i) =>
      i < items.length - 1 ? i + 1 : 0 // wrap around
    );
  }, [items]);

  const onShare = () => {
    if (!lightbox) return;
    const shareData = {
      title: "The Beth Branson Memorial Gallery",
      text: lightbox.caption || "",
      url: window.location.href,
    };
    if (navigator.share) navigator.share(shareData).catch(() => {});
  };

  // 🚀 Handle keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;

    const handleKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex, showPrev, showNext]);

  // 📴 Prevent background scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = lightboxIndex !== null ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [lightboxIndex]);

  // 📱 Handle swipe gestures
  const [touchStartX, setTouchStartX] = useState(null);

  const handleTouchStart = (e) => setTouchStartX(e.touches[0].clientX);
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX;
    if (diff > 50) showPrev(); // swipe right
    if (diff < -50) showNext(); // swipe left
    setTouchStartX(null);
  };

  return (
    <section className="gallery-page">
      <div className="header">
        <h1>The Beth Branson Memorial Gallery</h1>
        <p className="intro">
          A collection of photos and videos from past memorial matches,
          celebrating Beth’s legacy both on and off the pitch.
        </p>
      </div>

      <div className="masonry">
        {items.map((m) => (
          <figure key={m.id} className="tile" onClick={() => openItem(m)}>
            {m.type === "image" ? (
              <div>
              <img
                src={m.src}
                alt={m.caption || "Gallery image"}
                loading="lazy"
              />
              {m.caption && <figcaption>{m.caption}</figcaption>}
              </div>
            ) : (
              <video src={m.src} preload="metadata" />
            )}
          </figure>
        ))}
      </div>

      {visibleCount < files.length && (
        <div className="load-more-wrap">
          <button
            className="load-more"
            onClick={() =>
              setVisibleCount((c) => Math.min(c + PAGE_SIZE, files.length))
            }
          >
            Load More
          </button>
        </div>
      )}

      {lightbox && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <button
              className="lb-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              ✕
            </button>
            <button className="lb-share" onClick={onShare} aria-label="Share">
              ⤴
            </button>

            <button className="lb-prev" onClick={showPrev} aria-label="Previous">
              ←
            </button>
            <button className="lb-next" onClick={showNext} aria-label="Next">
              →
            </button>

            {lightbox.type === "image" ? (
              <img src={lightbox.src} alt={lightbox.caption || "Media"} />
            ) : (
              <video
                src={lightbox.src}
                controls
                playsInline
                preload="metadata"
              />
            )}
            {lightbox.caption && (
              <div className="lb-caption">{lightbox.caption}</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}